import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { 
    Typography, 
    SxProps, 
    Theme, 
    LinearProgress, 
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Box,
} from '@mui/material';

import Row from './Row';
import Column from './Column';
import ColumnFilterPopover from './ColumnFilterPopover';
import { SortTableColumnIcon } from './table/SortTableColumnIcon';
import { CenteredMessage, ResizeHandle, VirtualCell, VirtualRow } from './table/table-styles-components';
import { HORIZONTALLY_SCROLLED_CLASS, PinnedColumnShadow } from './table/PinnedColumnShadow';
import { ColumnFiltersConfig, SimpleColumnDef, SortingState } from '../../../types/table';

export interface CoreVirtualizedTableProps<TData> {
    /** Data array to display in the table */
    data: TData[];
    /** Column definitions */
    columns: SimpleColumnDef<TData>[];
    /** Height of the table container (default: '500px') */
    height?: string | number;
    /** Height of each row in pixels (default: 52) */
    rowHeight?: number;
    /** Number of rows to render outside visible area (default: 5) */
    overscan?: number;
    /** Minimum width of the table (default: 1000) */
    minWidth?: number;
    /** Current sorting state */
    sorting?: SortingState;
    /** Callback when sorting changes */
    onSortingChange?: (sorting: SortingState) => void;
    /** Enable/disable sorting (default: true) */
    enableSorting?: boolean;
    /** Enable/disable manual sorting (default: false) */
    enableManualSorting?: boolean;
    /** Custom styles for the container */
    containerSx?: SxProps<Theme>;
    /** Callback when a row is clicked */
    onRowClick?: (row: TData) => void;
    /** Show loading state with headers and linear progress */
    isLoading?: boolean;
    /** Empty state message */
    emptyMessage?: string;
    /** Get unique key for each row */
    getRowId?: (row: TData) => string;
    /** Callback to load more data when scrolled near the bottom */
    onLoadMore?: () => void;
    /** Whether there is more data to load */
    hasMore?: boolean;
    /** Whether more data is currently being loaded */
    isLoadingMore?: boolean;
    /** Distance from bottom (in pixels) at which to trigger load more (default: 200) */
    loadMoreThreshold?: number;
    /** ID of the selected row to highlight */
    selectedRowId?: string | null;
    /** Function to get custom row styles based on row data */
    getRowStyle?: (row: TData) => SxProps<Theme>;
    /** Column filters configuration for Excel-like filtering */
    columnFilters?: ColumnFiltersConfig;
    /** Enable column resizing (default: false) */
    isResizable?: boolean;
    /** When true, omits extra styles applied to the first column via &:first-of-type */
    disableFirstOfTypeStyles?: boolean;
    /** When true, omits the extra right padding applied to the last column via &:last-of-type, keeping it at the standard 12px */
    disableLastOfTypeStyles?: boolean;
    /** When true, columns marked as pinned (via columnFilters.isColumnPinned) stay fixed to the left while the rest of the columns scroll horizontally */
    pinColumns?: boolean;
}

/** Get value from row using accessor key (supports dot notation) */
function getValueByPath<TData>(row: TData, path: string): any {
    return path.split('.').reduce((obj: any, key) => obj?.[key], row);
}

function CoreVirtualizedTable<TData>({
    data,
    columns,
    height = '500px',
    rowHeight = 52,
    overscan = 5,
    minWidth = 1000,
    sorting = [],
    onSortingChange,
    enableSorting = true,
    enableManualSorting = false,
    containerSx,
    onRowClick,
    isLoading = false,
    emptyMessage = 'No data available',
    getRowId,
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
    loadMoreThreshold = 200,
    selectedRowId,
    getRowStyle,
    columnFilters,
    isResizable = false,
    disableFirstOfTypeStyles = false,
    disableLastOfTypeStyles = false,
    pinColumns = false,
}: CoreVirtualizedTableProps<TData>) {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const loadMoreTriggeredRef = useRef(false);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    // Id of the column whose filter/sort popover is currently open, so its header cell keeps its hover background
    const [openFilterColumnId, setOpenFilterColumnId] = useState<string | null>(null);

    // Column resize state
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
        const widths: Record<string, number> = {};
        columns.forEach(col => {
            if (col.width) {
                widths[col.id] = col.width;
            }
        });
        return widths;
    });
    const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);
    const resizeStartXRef = useRef<number>(0);
    const resizeStartWidthRef = useRef<number>(0);

    // Fallback width for columns that don't specify width/minWidth, sized to fit the
    // uppercase/letter-spaced header text plus its sort/filter icon
    const getDefaultColumnWidth = useCallback((column: SimpleColumnDef<TData>): number => {
        const headerLength = typeof column.header === 'string' ? column.header.length : 0;
        return Math.max(100, headerLength * 10 + 64);
    }, []);

    // Calculate total width of all columns (base widths)
    const calculateTotalColumnWidth = useCallback((): number => {
        return columns.reduce((total, col) => {
            const width = columnWidths[col.id] || col.width || col.minWidth || getDefaultColumnWidth(col);
            return total + (typeof width === 'number' ? width : getDefaultColumnWidth(col));
        }, 0);
    }, [columns, columnWidths, getDefaultColumnWidth]);

    // Distribute extra space proportionally among columns
    const getColumnWidth = useCallback((column: SimpleColumnDef<TData>): number => {
        const baseWidth = columnWidths[column.id] || column.width || column.minWidth || getDefaultColumnWidth(column);
        const numericBaseWidth = typeof baseWidth === 'number' ? baseWidth : getDefaultColumnWidth(column);

        // If container width is not set, return base width
        if (containerWidth === 0) {
            return numericBaseWidth;
        }

        // Calculate total base width
        const totalBaseWidth = calculateTotalColumnWidth();
        
        // If total is less than container width, distribute extra space proportionally
        // This ensures the table fills the container width
        if (totalBaseWidth < containerWidth && totalBaseWidth > 0) {
            const extraSpace = containerWidth - totalBaseWidth;
            const proportion = numericBaseWidth / totalBaseWidth;
            const distributed = numericBaseWidth + (extraSpace * proportion);
            return column.maxWidth ? Math.min(distributed, column.maxWidth) : distributed;
        }

        // If total is greater than or equal to container width, use base width
        // This allows horizontal scrolling when columns are resized wider
        return numericBaseWidth;
    }, [columnWidths, containerWidth, calculateTotalColumnWidth, getDefaultColumnWidth]);

    // Calculate total width after distribution (final widths)
    const calculateTotalDistributedWidth = useCallback((): number => {
        if (containerWidth === 0) {
            return calculateTotalColumnWidth();
        }
        return columns.reduce((total, col) => {
            return total + getColumnWidth(col);
        }, 0);
    }, [columns, containerWidth, calculateTotalColumnWidth, getColumnWidth]);

    // Left offset (in px) for each pinned column, in left-to-right order, plus which pinned column is last (for the boundary shadow)
    const { pinnedLeftOffsets, lastPinnedColumnId } = useMemo(() => {
        if (!pinColumns || !columnFilters?.isColumnPinned) {
            return { pinnedLeftOffsets: {} as Record<string, number>, lastPinnedColumnId: null as string | null };
        }

        const offsets: Record<string, number> = {};
        let cumulativeLeft = 0;
        let lastId: string | null = null;

        columns.forEach((column) => {
            if (columnFilters.isColumnPinned!(column.id)) {
                offsets[column.id] = cumulativeLeft;
                cumulativeLeft += getColumnWidth(column);
                lastId = column.id;
            }
        });

        return { pinnedLeftOffsets: offsets, lastPinnedColumnId: lastId };
    }, [pinColumns, columnFilters, columns, getColumnWidth]);

    // Get cell value for a column
    const getCellValue = useCallback((row: TData, column: SimpleColumnDef<TData>): any => {
        if (column.accessorFn) {
            return column.accessorFn(row);
        }
        if (column.accessorKey) {
            return getValueByPath(row, column.accessorKey as string);
        }
        return undefined;
    }, []);

    // Sort data based on sorting state
    const sortedData = useMemo(() => {
        if (!enableSorting || sorting.length === 0 || !enableManualSorting) {
            return data;
        }

        const sortConfig = sorting[0]; // Support single column sorting
        if (!sortConfig) return data;

        const column = columns.find(c => c.id === sortConfig.id);
        if (!column) return data;

        return [...data].sort((a, b) => {
            const aVal = getCellValue(a, column);
            const bVal = getCellValue(b, column);

            // Handle null/undefined - nulls are treated as the lowest value
            // ASC: nulls first (oldest/smallest), DESC: nulls last (oldest/smallest at bottom)
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return sortConfig.desc ? 1 : -1;
            if (bVal == null) return sortConfig.desc ? -1 : 1;

            // Compare values
            let comparison = 0;
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                comparison = aVal.localeCompare(bVal);
            } else if (typeof aVal === 'number' && typeof bVal === 'number') {
                comparison = aVal - bVal;
            } else if (aVal instanceof Date && bVal instanceof Date) {
                comparison = aVal.getTime() - bVal.getTime();
            } else {
                comparison = String(aVal).localeCompare(String(bVal));
            }

            return sortConfig.desc ? -comparison : comparison;
        });
    }, [data, sorting, enableSorting, columns, getCellValue]);

    // Generate row IDs
    const rows = useMemo(() => {
        return sortedData.map((row, index) => ({
            id: getRowId ? getRowId(row) : String(index),
            original: row,
        }));
    }, [sortedData, getRowId]);

    // Custom virtualization state
    const [scrollTop, setScrollTop] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);

    // Calculate total height of all rows
    const totalSize = rows.length * rowHeight;

    // Calculate which rows to render based on scroll position
    const virtualRows = useMemo(() => {
        // Use a default visible height if container hasn't been measured yet
        // This ensures rows are rendered on initial mount
        const visibleHeight = containerHeight > 0 ? containerHeight : 500;

        // Calculate the range of visible rows
        const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
        const endIndex = Math.min(
            rows.length - 1,
            Math.ceil((scrollTop + visibleHeight) / rowHeight) + overscan
        );

        // Generate virtual row items
        const items: { index: number; start: number }[] = [];
        for (let i = startIndex; i <= endIndex; i++) {
            items.push({
                index: i,
                start: i * rowHeight,
            });
        }

        return items;
    }, [scrollTop, containerHeight, rows.length, rowHeight, overscan]);

    // Track if we should show the virtualized table (not loading and has rows)
    const showVirtualizedTable = !isLoading && rows.length > 0;

    // Update container height and width on mount and resize
    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container) return;

        const updateDimensions = () => {
            const newHeight = container.clientHeight;
            const newWidth = container.clientWidth;
            if (newHeight > 0) {
                setContainerHeight(newHeight);
            }
            if (newWidth > 0) {
                setContainerWidth(newWidth);
            }
        };

        // Initial measurement
        updateDimensions();

        // Also measure after a short delay to handle layout timing
        const timeoutId = setTimeout(updateDimensions, 100);

        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(container);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
        };
    }, [isLoading, rows.length]);

    // Reset the load more trigger when loading completes or data changes
    useEffect(() => {
        if (!isLoadingMore) {
            loadMoreTriggeredRef.current = false;
        }
    }, [isLoadingMore, data.length]);

    // Reset scroll position when data is completely replaced (e.g., navigating back)
    useEffect(() => {
        // Reset scroll when data changes from empty to having items
        if (data.length > 0 && scrollTop > 0) {
            const container = tableContainerRef.current;
            if (container && container.scrollTop === 0) {
                // Container scroll is at 0 but state says otherwise - sync state
                setScrollTop(0);
            }
        }
    }, [data.length]);

    // Check if we should load more based on scroll position
    const checkShouldLoadMore = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container || !onLoadMore || !hasMore || isLoadingMore || loadMoreTriggeredRef.current) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = container;

        // A container that's hidden (e.g. display:none while its table is kept mounted
        // off-screen, like PolicyQuestionsTable during the Ask Reve flow) reports 0 for
        // all three of these, which reads as "already at the bottom" -- without this
        // guard it would fetch every remaining page in a tight loop in the background.
        if (clientHeight === 0) {
            return;
        }

        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        if (distanceFromBottom <= loadMoreThreshold) {
            loadMoreTriggeredRef.current = true;
            onLoadMore();
        }
    }, [onLoadMore, hasMore, isLoadingMore, loadMoreThreshold]);

    // Handle scroll events for virtualization and infinite scroll
    const handleScroll = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container) return;

        setScrollTop(container.scrollTop);
        // Toggled directly via the DOM (not React state) so horizontal scrolling doesn't trigger a re-render;
        // PinnedColumnShadow's CSS reacts to this class on an ancestor to show/hide itself.
        container.classList.toggle(HORIZONTALLY_SCROLLED_CLASS, container.scrollLeft > 0);
        checkShouldLoadMore();
    }, [checkShouldLoadMore]);

    useEffect(() => {
        // Only attach scroll listener when virtualized table is shown
        if (!showVirtualizedTable) return;
        
        const container = tableContainerRef.current;
        if (!container) return;

        console.log('Attaching scroll listener to:', container);
        container.addEventListener('scroll', handleScroll, { passive: true });
        
        // Also check on initial render and when data changes (in case content doesn't fill the viewport)
        checkShouldLoadMore();

        return () => {
            console.log('Removing scroll listener');
            container.removeEventListener('scroll', handleScroll);
        };
    }, [showVirtualizedTable, handleScroll, checkShouldLoadMore]);

    // Handle column sorting
    const handleSort = useCallback((columnId: string) => {
        if (!onSortingChange) return;

        const currentSort = sorting.find(s => s.id === columnId);
        let newSorting: SortingState;

        if (!currentSort) {
            // Not sorted -> sort ascending
            newSorting = [{ id: columnId, desc: false }];
        } else if (!currentSort.desc) {
            // Ascending -> sort descending
            newSorting = [{ id: columnId, desc: true }];
        } else {
            // Descending -> clear sort
            newSorting = [];
        }

        onSortingChange(newSorting);
    }, [sorting, onSortingChange]);

    // Set sort direction for a column directly (used by the column filter menu's Sort control)
    const handleSortChange = useCallback((columnId: string, direction: 'asc' | 'desc' | null) => {
        if (!onSortingChange) return;

        if (direction === null) {
            onSortingChange(sorting.filter(s => s.id !== columnId));
        } else {
            onSortingChange([{ id: columnId, desc: direction === 'desc' }]);
        }
    }, [sorting, onSortingChange]);

    // Get sort direction for a column
    const getSortDirection = useCallback((columnId: string): 'asc' | 'desc' | false => {
        const sortConfig = sorting.find(s => s.id === columnId);
        if (!sortConfig) return false;
        return sortConfig.desc ? 'desc' : 'asc';
    }, [sorting]);

    // Column resize handlers
    const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent, columnId: string) => {
        e.preventDefault();
        e.stopPropagation();

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const column = columns.find(c => c.id === columnId);
        const currentWidth = columnWidths[columnId] ?? column?.width ?? 100;

        resizeStartXRef.current = clientX;
        resizeStartWidthRef.current = currentWidth;
        setResizingColumnId(columnId);
    }, [columns, columnWidths]);

    // Handle mouse/touch move for resizing
    useEffect(() => {
        if (!resizingColumnId) return;

        const handleMove = (e: MouseEvent | TouchEvent) => {
            const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
            const delta = clientX - resizeStartXRef.current;
            const column = columns.find(c => c.id === resizingColumnId);
            const minWidth = column?.minWidth ?? 50;
            const newWidth = Math.max(minWidth, resizeStartWidthRef.current + delta);

            setColumnWidths(prev => ({
                ...prev,
                [resizingColumnId]: newWidth,
            }));
        };

        const handleEnd = () => {
            setResizingColumnId(null);
        };

        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleMove);
        document.addEventListener('touchend', handleEnd);

        return () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleEnd);
            document.removeEventListener('touchmove', handleMove);
            document.removeEventListener('touchend', handleEnd);
        };
    }, [resizingColumnId, columns]);

    // Render table header (used in both loading and normal states)
    const renderTableHeader = (useFlexLayout = false) => (
        <TableHead
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: useFlexLayout ? 'block' : 'table-header-group',
            }}
        >
            <TableRow
                sx={{
                    display: useFlexLayout ? 'flex' : 'table-row',
                    width: useFlexLayout ? '100%' : 'auto',
                }}
            >
                {columns.map((column) => {
                    const canSort = enableSorting && column.enableSorting !== false;
                    const sortDirection = getSortDirection(column.id);
                    const colWidth = getColumnWidth(column);
                    const isResizingThis = resizingColumnId === column.id;
                    const isPinnedColumn = pinColumns && !!columnFilters?.isColumnPinned?.(column.id);
                    const showPinnedShadow = isPinnedColumn && column.id === lastPinnedColumnId;

                    return (
                        <TableCell
                            key={column.id}
                            onClick={canSort && !resizingColumnId ? () => handleSort(column.id) : undefined}
                            sx={{
                                textAlign: 'left',
                                padding: useFlexLayout ? '0 12px' : { xs: '8px 10px', sm: '10px 12px', md: '10px 12px', lg: '12px 16px' },
                                backgroundColor: openFilterColumnId === column.id ? '#F3F4F6' : 'white',
                                cursor: canSort && !resizingColumnId ? 'pointer' : 'default',
                                userSelect: 'none',
                                width: typeof colWidth === 'number' ? colWidth : colWidth,
                                minWidth: column.minWidth,
                                maxWidth: column.maxWidth,
                                ...(column.maxWidth && { padding: 0 }),
                                position: isPinnedColumn ? 'sticky' : 'relative',
                                ...(isPinnedColumn && { left: pinnedLeftOffsets[column.id], zIndex: 2 }),
                                display: useFlexLayout ? 'flex' : 'table-cell',
                                alignItems: useFlexLayout ? 'center' : undefined,
                                height: useFlexLayout ? rowHeight : 'auto',
                                flexShrink: useFlexLayout ? 0 : undefined,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:hover': canSort && !resizingColumnId ? {
                                    backgroundColor: '#F3F4F6',
                                } : {},
                                '&:hover .resize-handle': {
                                    opacity: 1,
                                },
                                ...(!disableFirstOfTypeStyles && {
                                    '&:first-of-type': {
                                        paddingLeft: useFlexLayout ? '20px' : { xs: '12px', sm: '16px', md: '16px', lg: '20px' },
                                    },
                                }),
                                ...(!disableLastOfTypeStyles && {
                                    '&:last-of-type': {
                                        paddingRight: useFlexLayout ? '20px' : { xs: '12px', sm: '16px', md: '16px', lg: '20px' },
                                    },
                                }),
                            }}
                        >
                            <Row
                                sx={{
                                    alignItems: 'center',
                                    justifyContent: column.headerAlign === 'center' ? 'center' : column.headerAlign === 'right' ? 'flex-end' : 'flex-start',
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        minWidth: 0,
                                    }}
                                >
                                    {column.header}
                                </Typography>

                                {canSort && sortDirection && (
                                    <SortTableColumnIcon direction={sortDirection} />
                                )}

                                {/* Column Filter */}
                                {columnFilters &&
                                 column.header &&
                                 (!columnFilters.filterableColumns ||
                                  columnFilters.filterableColumns.includes(column.id)) && (
                                    <Box sx={{ flexShrink: 0 }}>
                                        <ColumnFilterPopover
                                            columnId={column.id}
                                            columnLabel={column.header}
                                            options={columnFilters.getOptions(column.id)}
                                            getOptionLabel={columnFilters.getOptionLabel ? (value) => columnFilters.getOptionLabel!(column.id, value) : undefined}
                                            selectedValues={columnFilters.filters[column.id]?.values ?? []}
                                            selectedOperator={columnFilters.filters[column.id]?.operator}
                                            onApply={columnFilters.onApply}
                                            onClear={columnFilters.onClear}
                                            isActive={!!columnFilters.filters[column.id]?.values?.length}
                                            filterType={columnFilters.columnFilterTypes?.[column.id] || 'list'}
                                            maxDate={columnFilters.columnFilterMaxDate?.[column.id]}
                                            placeholder={columnFilters.columnFilterPlaceholders?.[column.id]}
                                            sortDirection={canSort ? sortDirection : undefined}
                                            isPinned={columnFilters.isColumnPinned?.(column.id)}
                                            onTogglePin={columnFilters.onTogglePin ? () => columnFilters.onTogglePin!(column.id) : undefined}
                                            canPin={columnFilters.canPinColumn ? columnFilters.canPinColumn(column.id) : true}
                                            onOpenChange={(isOpen) => setOpenFilterColumnId(isOpen ? column.id : null)}
                                        />
                                    </Box>
                                )}
                            </Row>
                            {/* Resize Handle */}
                            {isResizable && (
                                <ResizeHandle
                                    className="resize-handle"
                                    isResizing={isResizingThis}
                                    onMouseDown={(e) => handleResizeStart(e, column.id)}
                                    onTouchStart={(e) => handleResizeStart(e, column.id)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            )}
                            {showPinnedShadow && <PinnedColumnShadow />}
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );

    // Loading state - show headers with linear progress
    if (isLoading) {
        return (
            <Column sx={{ width: '100%', height: '100%', ...containerSx }}>
                <TableContainer ref={tableContainerRef} sx={{ height, position: 'relative' }}>
                    <Table
                        sx={{
                            width: containerWidth > 0 ? containerWidth : '100%',
                            borderCollapse: 'collapse',
                            minWidth: containerWidth > 0 ? containerWidth : minWidth,
                            tableLayout: 'fixed',
                            position: 'relative',
                        }}
                    >
                        {renderTableHeader()}
                    </Table>
                    
                    {/* Linear Progress Loader */}
                    <LinearProgress 
                        sx={{ 
                            width: tableContainerRef.current?.style?.width || '100%',
                            height: 3,
                            backgroundColor: '#E5E7EB',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: 'primary.main',
                            },
                        }} 
                    />
                    
                    {/* Loading message */}
                    <CenteredMessage>
                        <Typography color="text.secondary">
                            Loading data...
                        </Typography>
                    </CenteredMessage>
                </TableContainer>
            </Column>
        );
    }

    // Empty state
    if (rows.length === 0) {
        return (
            <Column sx={{ width: '100%', height: '100%', ...containerSx }}>
                <TableContainer ref={tableContainerRef} sx={{ height, position: 'relative' }}>
                    <Table
                        sx={{
                            width: containerWidth > 0 ? containerWidth : '100%',
                            minWidth: containerWidth > 0 ? containerWidth : minWidth,
                            borderCollapse: 'collapse',
                            tableLayout: 'fixed',
                        }}
                    >
                        {renderTableHeader()}
                    </Table>
                    
                    {/* Empty message */}
                    <CenteredMessage>
                        <Typography color="text.secondary">
                            {emptyMessage}
                        </Typography>
                    </CenteredMessage>
                </TableContainer>
            </Column>
        );
    }

    return (
        <Column sx={{ width: '100%', height: '100%', ...containerSx }}>
            {/* Scrollable Table Container */}
            <TableContainer
                ref={tableContainerRef}
                sx={{
                    height,
                    position: 'relative',
                    overflow: 'auto',
                }}
            >
                {/* Wrapper with table width so progress + table scroll together horizontally */}
                <Box
                    sx={{
                        width: containerWidth === 0 ? '100%' : Math.max(containerWidth, calculateTotalDistributedWidth()),
                        minWidth: containerWidth > 0 ? containerWidth : minWidth,
                    }}
                >
                    {/* Linear progress: full table width, sticky below header */}
                    <Box
                        sx={{
                            position: 'sticky',
                            top: rowHeight - 3,
                            left: 0,
                            width: '100%',
                            height: isLoading || isLoadingMore ? 3 : 0,
                            overflow: 'hidden',
                            zIndex: 10,
                            pointerEvents: 'none',
                        }}
                    >
                        <LinearProgress
                            variant="indeterminate"
                            sx={{
                                width: '100%',
                                height: 3,
                                backgroundColor: '#E5E7EB',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: 'primary.main',
                                },
                            }}
                        />
                    </Box>
                    {/* Table */}
                    <Table
                        sx={{
                            width: '100%',
                            minWidth: containerWidth > 0 ? containerWidth : minWidth,
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            tableLayout: 'fixed',
                        }}
                    >
                        {renderTableHeader(true)}
               
                    {/* Virtualized Rows */}
                    <TableBody
                        component="tbody"
                        sx={{
                            display: 'block',
                            position: 'relative',
                            height: totalSize > 0 ? totalSize : 'auto',
                            width: containerWidth > 0 
                                ? Math.max(containerWidth, calculateTotalDistributedWidth())
                                : '100%',
                        }}
                    >
                        {virtualRows.map((virtualRow) => {
                            const row = rows[virtualRow.index];
                            const isSelected = selectedRowId && row.id === selectedRowId;
                            const customRowStyle = getRowStyle?.(row.original) ?? {};

                            return (
                                <VirtualRow
                                    key={row.id}
                                    translateY={virtualRow.start}
                                    rowHeight={rowHeight}
                                    isSelected={!!isSelected}
                                    isClickable={!!onRowClick}
                                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                                    style={customRowStyle as React.CSSProperties}
                                >
                                    {columns.map((column) => {
                                        const value = getCellValue(row.original, column);
                                        const cellContent = column.cell 
                                            ? column.cell(value, row.original) 
                                            : value;
                                        const colWidth = getColumnWidth(column);
                                        const isPinnedColumn = pinColumns && !!columnFilters?.isColumnPinned?.(column.id);
                                        const showPinnedShadow = isPinnedColumn && column.id === lastPinnedColumnId;

                                        return (
                                            <VirtualCell
                                                key={column.id}
                                                width={typeof colWidth === 'number' ? colWidth : colWidth}
                                                minWidth={column.minWidth}
                                                maxWidth={column.maxWidth}
                                                $disableFirstOfTypeStyles={disableFirstOfTypeStyles}
                                                $disableLastOfTypeStyles={disableLastOfTypeStyles}
                                                $isPinned={isPinnedColumn}
                                                $pinnedLeft={pinnedLeftOffsets[column.id]}
                                                $backgroundColor={isPinnedColumn ? (isSelected ? '#F9FAFB' : 'white') : undefined}
                                                $allowShadowOverflow={showPinnedShadow}
                                            >
                                                {showPinnedShadow ? (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            minWidth: 0,
                                                            flex: 1,
                                                        }}
                                                    >
                                                        {cellContent}
                                                    </Box>
                                                ) : cellContent}
                                                {showPinnedShadow && <PinnedColumnShadow />}
                                            </VirtualCell>
                                        );
                                    })}
                                </VirtualRow>
                            );
                        })}
                    </TableBody>
                </Table>
                </Box>

                {/* Loading More Indicator */}
                {isLoadingMore && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 2,
                            gap: 1,
                        }}
                    >
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Loading more...
                        </Typography>
                    </Box>
                )}
            </TableContainer>
        </Column>
    );
}

export default CoreVirtualizedTable;
